"use client";

import {
  AgentMessage,
  ProseBlockquote,
  ProseEm,
  ProseH2,
  ProseH3,
  ProseH4,
  ProseHr,
  ProseInlineCode,
  ProseLi,
  ProseLink,
  ProseOl,
  ProseP,
  ProsePre,
  ProseStrong,
  ProseTable,
  ProseTbody,
  ProseTd,
  ProseTh,
  ProseThead,
  ProseUl,
  Typography,
  UserMessage,
} from "@health/ui";
import type { ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const markdownComponents: Components = {
  h1: ({ children }) => (
    <Typography.Question className="mt-6 sm:mb-1.5 first:mt-0">{children}</Typography.Question>
  ),
  h2: ProseH2,
  h3: ProseH3,
  h4: ProseH4,
  p: ProseP,
  hr: ProseHr,
  blockquote: ProseBlockquote,
  ul: ProseUl,
  ol: ProseOl,
  li: ProseLi,
  strong: ProseStrong,
  em: ProseEm,
  a: ({ href, children }) => (
    <ProseLink href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </ProseLink>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.startsWith("language-");
    if (isBlock) {
      return <code className={`${className} block`}>{children}</code>;
    }
    return <ProseInlineCode>{children}</ProseInlineCode>;
  },
  pre: ProsePre,
  table: ProseTable,
  thead: ProseThead,
  th: ProseTh,
  td: ProseTd,
  tbody: ProseTbody,
};

/**
 * Default text renderer using react-markdown with the same component map
 * as MessageBubble. Pass this as the `renderText` prop to `ChatFeed`.
 */
export function defaultRenderText(text: string, role: "user" | "assistant"): ReactNode {
  if (role === "user") {
    return <UserMessage>{text}</UserMessage>;
  }

  return (
    <AgentMessage>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {text}
      </ReactMarkdown>
    </AgentMessage>
  );
}
